import { useParams, Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useJob, useUpdateJob, useParseJob, useResearchJob } from "@/hooks/useJobs";
import { createPipeline } from "@/api/pipeline";
import { listPrompts, type PromptTemplate } from "@/api/prompts";
import { PromptTemplatePicker } from "@/components/jobs/prompt-template-picker";
import { PromptPreviewModal } from "@/components/jobs/prompt-preview-modal";
import { JobScoreControl } from "@/components/jobs/job-score-control";
import { useEffect, useState } from "react";
import { cn } from "@/cn";
import { useContacts } from "@/hooks/useContacts";
import { ContactForm, ContactList } from "@/components/companies/contact-panel";
import { FollowUpPanel } from "@/components/network/followup-panel";
import { JobDocumentSendsPanel } from "@/components/documents/job-document-sends-panel";
import { DocumentLibraryPanel } from "@/components/documents/document-library-panel";

type ParseStatus = {
  method?: "ai" | "offline";
  success?: boolean;
  message?: string;
};

export function JobsDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { data: job, isLoading, error } = useJob(id);
  const { data: contactsData } = useContacts(
    job ? { companySlug: job.companySlug } : undefined,
  );
  const jobContacts = contactsData?.contacts ?? [];
  const [attributionContactId, setAttributionContactId] = useState("");
  const [referralBy, setReferralBy] = useState("");

  const updateJob = useUpdateJob(id ?? "");
  const parseJob = useParseJob();
  const researchJob = useResearchJob();
  const { data: researchTemplates = [], isLoading: templatesLoading } = useQuery<PromptTemplate[]>({
    queryKey: ["prompts", "category", "job-research"],
    queryFn: async () => (await listPrompts({ category: "job-research" })).prompts,
    staleTime: 30_000,
  });

  const [jobDescription, setJobDescription] = useState("");
  const [notes, setNotes] = useState("");
  const [researchError, setResearchError] = useState("");
  const [selectedPromptKind, setSelectedPromptKind] = useState("");
  const [parseStatus, setParseStatus] = useState<ParseStatus>({});
  const [previewPromptKind, setPreviewPromptKind] = useState<string | null>(null);

  const selectedTemplate = researchTemplates.find((t) => t.kind === selectedPromptKind);

  useEffect(() => {
    if (!selectedPromptKind && researchTemplates.length > 0) {
      const standard = researchTemplates.find((t) => t.kind === "job-research");
      setSelectedPromptKind(standard?.kind ?? researchTemplates[0].kind);
    }
  }, [researchTemplates, selectedPromptKind]);

  useEffect(() => {
    if (job) {
      setJobDescription(job.jobDescription ?? "");
      setNotes(job.notes ?? "");
      setAttributionContactId(job.attributionContactId ?? "");
      setReferralBy(job.referralBy ?? "");
    }
  }, [job]);

  const createPipelineMutation = useMutation({
    mutationFn: (jobId: string) => createPipeline(jobId),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["pipeline"] });
      navigate(`/pipeline/${data.pipeline.id}?stage=Evaluated`);
    },
  });

  const handleSaveDescription = () => {
    if (!id) return;
    updateJob.mutate({ jobDescription } as Record<string, unknown> as any);
  };

  const handleSaveAttribution = () => {
    if (!id) return;
    const contact = jobContacts.find((c) => c.id === attributionContactId);
    updateJob.mutate({
      attributionContactId: attributionContactId || undefined,
      referralBy: referralBy.trim() || contact?.name || undefined,
    } as Record<string, unknown> as never);
  };

  const handleSaveNotes = () => {
    if (!id) return;
    updateJob.mutate({ notes } as Record<string, unknown> as any);
  };

  const handleParse = (mode: "ai" | "offline") => {
    if (!id) return;
    setParseStatus({});
    parseJob.mutate(
      { jobId: id, mode },
      {
        onSuccess: (result) => {
          if (result.success) {
            setParseStatus({
              method: result.method,
              success: true,
              message:
                mode === "ai"
                  ? "Job details extracted with AI and saved."
                  : "Job page scraped and description saved.",
            });
          } else {
            setParseStatus({
              method: mode,
              success: false,
              message: result.error ?? "Could not extract details from the URL.",
            });
          }
        },
        onError: (err) => {
          setParseStatus({
            method: mode,
            success: false,
            message: err instanceof Error ? err.message : "Parse failed.",
          });
        },
      },
    );
  };

  const handleResearch = () => {
    if (!id || !selectedPromptKind) return;
    setResearchError("");
    researchJob.mutate(
      { jobId: id, promptKind: selectedPromptKind },
      {
        onSuccess: (result) => {
          if (!result?.success) {
            setResearchError(result?.error ?? "Research failed.");
          }
        },
        onError: (err) => {
          setResearchError(err instanceof Error ? err.message : "Research failed.");
        },
      },
    );
  };

  const handleCreatePipeline = () => {
    if (!id) return;
    createPipelineMutation.mutate(id);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-muted border-t-brand" />
      </div>
    );
  }

  if (error || !job) {
    return (
      <div className="mx-auto max-w-2xl">
        <Link to="/jobs" className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
          </svg>
          Back to Jobs
        </Link>
        <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-4 py-3">
          <p className="text-sm text-destructive">Job not found.</p>
        </div>
      </div>
    );
  }

  const hasDescription = Boolean(job.jobDescription?.trim());
  const hasUrl = Boolean(job.url);
  const canResearch = Boolean(job.company?.trim() && job.role?.trim());
  const isParsing = parseJob.isPending;
  const isResearching = researchJob.isPending;

  return (
    <div className="mx-auto max-w-2xl">
      <Link to="/jobs" className="mb-6 inline-flex items-center gap-1 text-sm text-brand hover:underline">
        <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18" />
        </svg>
        Back to Jobs
      </Link>

      {/* Job Header */}
      <div className="mb-6 rounded-lg border border-border bg-card p-6">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl font-bold text-foreground">
              {job.companySlug ? (
                <Link
                  to={`/companies/${job.companySlug}`}
                  className="hover:text-brand hover:underline"
                >
                  {job.company}
                </Link>
              ) : (
                job.company
              )}
            </h1>
            <p className="text-lg text-muted-foreground">{job.role}</p>
          </div>
          <div className="flex shrink-0 flex-col items-end gap-2">
            <span
              className={cn(
                "rounded-full px-2.5 py-0.5 text-xs font-medium",
                job.source === "scan"
                  ? "bg-sky-500/10 text-sky-400"
                  : job.source === "ai-search"
                    ? "bg-violet-500/10 text-violet-400"
                    : "bg-muted/30 text-muted-foreground",
              )}
            >
              {job.source === "scan"
                ? "Find Jobs"
                : job.source === "ai-search"
                  ? "AI Search"
                  : job.source}
            </span>
            {!job.pipelineId ? (
              <button
                onClick={handleCreatePipeline}
                disabled={createPipelineMutation.isPending}
                className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                {createPipelineMutation.isPending ? "Creating…" : "Create Pipeline"}
              </button>
            ) : (
              <Link
                to={`/pipeline/${job.pipelineId}?stage=${encodeURIComponent(job.stage ?? "Evaluated")}`}
                className="rounded-md bg-brand/10 px-3 py-1.5 text-xs font-medium text-brand transition hover:bg-brand/20"
              >
                View Pipeline
              </Link>
            )}
            {id && (
              <Link
                to={`/ai-assistant?jobId=${encodeURIComponent(id)}`}
                className="rounded-md border border-border px-3 py-1.5 text-xs font-medium text-foreground transition hover:bg-surface-hover"
              >
                Ask AI
              </Link>
            )}
          </div>
        </div>
        {job.url && (
          <a
            href={job.url}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-3 block truncate rounded-md bg-muted/30 px-3 py-2 text-xs text-brand hover:underline"
          >
            {job.url}
          </a>
        )}
      </div>

      {id && (
        <div className="mb-6">
          <JobScoreControl jobId={id} job={job} />
        </div>
      )}

      {/* AI workflows */}
      <div className="mb-6 space-y-4">
        {/* Step 1: Enrich */}
        <section className="rounded-lg border border-border bg-card p-5">
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Step 1</p>
            <h2 className="text-sm font-semibold text-foreground">Enrich from job URL</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Pull the job description and structured fields from the posting. Do this before research for better results.
            </p>
          </div>

          <div className="mb-4 flex flex-wrap gap-2 text-xs">
            <span
              className={`rounded-full px-2.5 py-0.5 font-medium ${
                hasUrl ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400" : "bg-muted/30 text-muted-foreground"
              }`}
            >
              {hasUrl ? "URL linked" : "No URL"}
            </span>
            <span
              className={`rounded-full px-2.5 py-0.5 font-medium ${
                hasDescription
                  ? "bg-emerald-500/10 text-emerald-600 dark:text-emerald-400"
                  : "bg-amber-500/10 text-amber-600 dark:text-amber-400"
              }`}
            >
              {hasDescription ? "Description loaded" : "Description missing"}
            </span>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => handleParse("ai")}
              disabled={isParsing || !hasUrl}
              className="rounded-md bg-brand px-3 py-2 text-xs font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
            >
              {isParsing && parseJob.variables?.mode !== "offline" ? "Fetching with AI…" : "Fetch with AI"}
            </button>
            <button
              type="button"
              onClick={() => handleParse("offline")}
              disabled={isParsing || !hasUrl}
              className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition hover:bg-muted/20 disabled:opacity-50"
            >
              {isParsing && parseJob.variables?.mode === "offline" ? "Scraping…" : "Scrape URL (offline)"}
            </button>
          </div>

          {!hasUrl && (
            <p className="mt-3 text-xs text-muted-foreground">Add a job URL to enable automatic enrichment.</p>
          )}

          {parseStatus.message && (
            <div
              className={`mt-3 rounded-md px-3 py-2 text-xs ${
                parseStatus.success
                  ? "border border-emerald-500/30 bg-emerald-500/5 text-emerald-700 dark:text-emerald-400"
                  : "border border-amber-500/30 bg-amber-500/5 text-amber-700 dark:text-amber-400"
              }`}
            >
              {parseStatus.message}
              {parseStatus.method && (
                <span className="ml-1 opacity-70">({parseStatus.method === "ai" ? "AI" : "offline"})</span>
              )}
            </div>
          )}
        </section>

        {/* Step 2: Research */}
        <section className="rounded-lg border border-border bg-card p-5">
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Step 2</p>
            <h2 className="text-sm font-semibold text-foreground">Research this job</h2>
            <p className="mt-1 text-xs text-muted-foreground">
              Pick a research template from the prompt library. Each template defines depth, focus, and what context it uses.
            </p>
          </div>

          {templatesLoading ? (
            <div className="flex items-center gap-2 py-4 text-xs text-muted-foreground">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted border-t-brand" />
              Loading research templates…
            </div>
          ) : (
            <PromptTemplatePicker
              templates={researchTemplates}
              selectedKind={selectedPromptKind}
              onSelect={setSelectedPromptKind}
              onViewPrompt={setPreviewPromptKind}
              disabled={isResearching}
            />
          )}

          <div className="mt-4 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <p className="text-xs text-muted-foreground">
              {selectedTemplate
                ? `Runs the “${selectedTemplate.name}” prompt against this job.`
                : "Select a template to continue."}
            </p>
            <div className="flex shrink-0 flex-wrap items-center gap-2">
              {selectedPromptKind && (
                <button
                  type="button"
                  onClick={() => setPreviewPromptKind(selectedPromptKind)}
                  className="rounded-md border border-border px-3 py-2 text-xs font-medium text-foreground transition hover:bg-muted/20"
                >
                  View prompt
                </button>
              )}
              <button
                type="button"
                onClick={handleResearch}
                disabled={isResearching || !selectedPromptKind || !canResearch}
                className="rounded-md bg-brand px-4 py-2 text-xs font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                {isResearching
                  ? "Running research…"
                  : selectedTemplate
                    ? `Run ${selectedTemplate.name}`
                    : "Run research"}
              </button>
            </div>
          </div>

          {!canResearch && (
            <p className="mt-2 text-xs text-amber-600 dark:text-amber-400">
              Company and role are required before research can run.
            </p>
          )}

          {researchError && (
            <div className="mt-3 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {researchError}
            </div>
          )}
        </section>
      </div>

      <PromptPreviewModal
        kind={previewPromptKind}
        open={previewPromptKind !== null}
        onClose={() => setPreviewPromptKind(null)}
      />

      {/* Job Details Grid */}
      <div className="mb-6 grid grid-cols-2 gap-4">
        {job.location && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Location</p>
            <p className="text-sm text-foreground">{job.location}</p>
          </div>
        )}
        {job.compensation && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Compensation</p>
            <p className="text-sm text-foreground">{job.compensation}</p>
          </div>
        )}
        {job.workMode && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Work Mode</p>
            <p className="text-sm text-foreground">{job.workMode}</p>
          </div>
        )}
        {job.payRange && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Pay Range</p>
            <p className="text-sm text-foreground">{job.payRange}</p>
          </div>
        )}
        {job.source && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Source</p>
            <p className="text-sm text-foreground">
              {job.source}
              {job.sourceDetail ? ` · ${job.sourceDetail}` : ""}
            </p>
          </div>
        )}
        {job.archetype && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Archetype</p>
            <p className="text-sm text-foreground">{job.archetype}</p>
          </div>
        )}
        {job.compEstimate && (
          <div className="rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Comp Estimate</p>
            <p className="text-sm text-foreground">{job.compEstimate}</p>
          </div>
        )}
        {job.tldr && (
          <div className="col-span-2 rounded-lg border border-border bg-card p-4">
            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">TL;DR</p>
            <p className="text-sm text-foreground">{job.tldr}</p>
          </div>
        )}
      </div>

      {/* Research output */}
      {(isResearching || job.research) && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <div className="mb-3 flex items-center justify-between gap-2">
            <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Research Notes</h3>
            {selectedTemplate && !isResearching && (
              <span className="rounded-full bg-muted/30 px-2 py-0.5 text-[10px] font-medium text-muted-foreground">
                {selectedTemplate.name}
              </span>
            )}
          </div>
          {isResearching ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <div className="h-3 w-3 animate-spin rounded-full border-2 border-muted border-t-brand" />
              Running {selectedTemplate?.name ?? "research"}…
            </div>
          ) : (
            <pre className="max-h-96 overflow-auto whitespace-pre-wrap text-xs text-muted-foreground">{job.research}</pre>
          )}
        </div>
      )}

      {/* Job Description */}
      <div className="mb-6 rounded-lg border border-border bg-card p-5">
        <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Job Description</h3>
        <p className="mb-3 text-xs text-muted-foreground">
          {hasDescription
            ? "Review or edit the description. Re-run Step 1 to refresh from the URL."
            : "No description yet — use Step 1 above to fetch from the job URL."}
        </p>
        <textarea
          value={jobDescription}
          onChange={(e) => setJobDescription(e.target.value)}
          rows={6}
          placeholder="Job description will appear here after enrichment…"
          className="w-full rounded-md border border-border bg-bg px-3 py-2 font-mono text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={handleSaveDescription}
            disabled={updateJob.isPending}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
          >
            {updateJob.isPending ? "Saving…" : "Save Description"}
          </button>
        </div>
      </div>

      {/* Notes */}
      <div className="mb-6 rounded-lg border border-border bg-card p-5">
        <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Notes</h3>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          placeholder="Add notes about this job…"
          className="w-full rounded-md border border-border bg-bg px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-brand"
        />
        <div className="mt-2 flex justify-end">
          <button
            onClick={handleSaveNotes}
            disabled={updateJob.isPending}
            className="rounded-md bg-brand px-3 py-1.5 text-xs font-medium text-brand-foreground transition hover:opacity-90 disabled:opacity-50"
          >
            {updateJob.isPending ? "Saving…" : "Save Notes"}
          </button>
        </div>
      </div>

      {job.companySlug && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Referral & attribution
          </h3>
          <div className="grid gap-3 sm:grid-cols-2">
            <select
              value={attributionContactId}
              onChange={(e) => {
                const id = e.target.value;
                setAttributionContactId(id);
                const contact = jobContacts.find((c) => c.id === id);
                if (contact) setReferralBy(contact.name);
              }}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            >
              <option value="">No linked contact</option>
              {jobContacts.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.role?.replace("_", " ") ?? "contact"})
                </option>
              ))}
            </select>
            <input
              type="text"
              placeholder="Referral by (display name)"
              value={referralBy}
              onChange={(e) => setReferralBy(e.target.value)}
              className="rounded-md border border-border bg-bg px-3 py-2 text-sm"
            />
          </div>
          <div className="mt-2 flex justify-end">
            <button
              type="button"
              onClick={handleSaveAttribution}
              disabled={updateJob.isPending}
              className="rounded-md border border-border px-3 py-1.5 text-xs font-medium hover:bg-muted/20 disabled:opacity-50"
            >
              Save attribution
            </button>
          </div>
        </div>
      )}

      {job.companySlug && (
        <div className="mb-6 rounded-lg border border-border bg-card p-5">
          <h3 className="mb-3 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Contacts at {job.company}
          </h3>
          <ContactList
            contacts={jobContacts.map((c) => ({
              id: c.id,
              name: c.name,
              email: c.email,
              linkedinUrl: c.linkedinUrl,
              role: c.role,
              title: c.title,
            }))}
            emptyMessage="No contacts linked to this job yet."
          />
          <div className="mt-4">
            <ContactForm companySlug={job.companySlug} jobId={job.id} />
          </div>
          <div className="mt-4">
            <FollowUpPanel job={job} contacts={jobContacts} compact />
          </div>
        </div>
      )}

      <div className="mb-6 space-y-4">
        <JobDocumentSendsPanel
          jobId={job.id}
          companyId={job.companySlug}
        />
        {job.companySlug && (
          <DocumentLibraryPanel
            linkedCompanyId={job.companySlug}
            defaultDocType="company_research"
            compact
          />
        )}
      </div>
    </div>
  );
}
